<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between py-4">
          <div class="flex items-center gap-3">
            <router-link
              to="/admin/dashboard"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                class="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </router-link>
            <div>
              <h1 class="text-xl font-bold text-gray-900">
                Ajouter une carte personnalisée
              </h1>
              <p class="text-sm text-gray-500">
                Créez une nouvelle carte pour votre catalogue
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Basic Information Card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">
            Informations de base
          </h2>
          <div class="space-y-4">
            <!-- Title -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Titre *</label
              >
              <input
                v-model="form.title"
                type="text"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all"
                placeholder="Ex: Carte iTunes 50$"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Description</label
              >
              <textarea
                v-model="form.description"
                rows="3"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all resize-none"
                placeholder="Décrivez cette carte cadeau..."
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <!-- Price -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Prix ({{ getCurrencyLabel(form.currency) }}) *</label
                >
                <input
                  v-model.number="form.price"
                  type="number"
                  required
                  min="0"
                  class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all"
                  placeholder="5000"
                />
              </div>

              <!-- Category -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Catégorie *</label
                >
                <select
                  v-model="form.category"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all"
                >
                  <option value="">Sélectionner...</option>
                  <option value="Payment Cards">Paiement</option>
                  <option value="Gaming">Jeux vidéo</option>
                  <option value="Entertainment">Divertissement</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Other">Autres</option>
                </select>
              </div>
            </div>

            <!-- Currency -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Devise *</label
              >
              <select
                v-model="form.currency"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all"
              >
                <option value="USD">USD - Dollar américain</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - Livre sterling</option>
                <option value="XOF">XOF - Franc CFA (UEMOA)</option>
                <option value="XAF">XAF - Franc CFA (BEAC)</option>
                <option value="CAD">CAD - Dollar canadien</option>
              </select>
            </div>

            <!-- Image URL -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Image de la carte</label
              >
              <FileUploadWidget
                v-model="form.imageUrl"
                :maxFiles="1"
                :acceptedFiles="['image']"
              />
              <p class="text-xs text-gray-500 mt-1">
                JPEG, PNG ou WebP (max 5MB)
              </p>
            </div>

            <!-- Stock Quantity (auto-calculated) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Quantité en stock</label
              >
              <input
                v-model.number="form.stock"
                type="number"
                disabled
                class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                placeholder="0"
              />
              <p class="text-xs text-gray-500 mt-1">
                Calculée automatiquement à partir du nombre de codes
              </p>
            </div>
          </div>
        </div>

        <!-- Redeem Codes Card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-semibold text-gray-900">
                Codes de recharge
              </h2>
              <p class="text-sm text-gray-500">
                Quantité: {{ redeemCodes.length }} code(s)
              </p>
            </div>
            <button
              type="button"
              @click="addRedeemCode"
              class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Ajouter un code
            </button>
          </div>

          <div v-if="redeemCodes.length > 0" class="space-y-3">
            <div
              v-for="(code, index) in redeemCodes"
              :key="index"
              class="flex items-center gap-3"
            >
              <span class="text-sm text-gray-500 w-20 flex-shrink-0"
                >Code #{{ index + 1 }}</span
              >
              <input
                v-model="redeemCodes[index]"
                type="text"
                required
                class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all font-mono text-sm"
                placeholder="XXXX-XXXX-XXXX-XXXX"
              />
              <button
                type="button"
                @click="removeRedeemCode(index)"
                class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Supprimer ce code"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>
          </div>

          <div v-else class="text-center py-8 bg-gray-50 rounded-xl">
            <svg
              class="w-12 h-12 text-gray-400 mx-auto mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <p class="text-gray-500">
              Aucun code de recharge. Cliquez sur "Ajouter un code" pour commencer.
            </p>
          </div>
        </div>

        <!-- Allowed Countries Card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-semibold text-gray-900">
                Pays autorisés
              </h2>
              <p class="text-sm text-gray-500">
                Sélectionnez les pays où cette carte est disponible
              </p>
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                @click="toggleAllCountries"
                class="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                {{
                  allCountriesSelected
                    ? 'Tout désélectionner'
                    : 'Tout sélectionner'
                }}
              </button>
              <button
                type="button"
                @click="countriesExpanded = !countriesExpanded"
                class="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <svg
                  v-if="!countriesExpanded"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Search and countries grid - only show when expanded -->
          <div v-if="countriesExpanded">
            <!-- Search -->
            <div class="mb-4">
              <input
                v-model="countrySearch"
                type="text"
                placeholder="Rechercher un pays..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all"
              />
            </div>

            <div v-if="filteredCountries.length > 0">
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                <label
                  v-for="country in filteredCountries"
                  :key="country.countryCode"
                  class="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  :class="{
                    'bg-blue-50 border-blue-300':
                      form.allowedCountries.includes(country.countryCode)
                  }"
                >
                  <input
                    v-model="form.allowedCountries"
                    type="checkbox"
                    :value="country.countryCode"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600"
                  />
                  <span class="text-lg">{{
                    getFlagEmoji(country.countryCode)
                  }}</span>
                  <span class="text-sm text-gray-700">{{
                    country.countryName
                  }}</span>
                </label>
              </div>
            </div>

            <div v-else class="text-center py-8 bg-gray-50 rounded-xl">
              <svg
                class="w-12 h-12 text-gray-400 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"
                />
              </svg>
              <p class="text-gray-500">Chargement des pays...</p>
            </div>
          </div>
        </div>

        <!-- Preview Card -->
        <div
          v-if="form.title"
          class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Aperçu</h2>
          <div class="max-w-xs">
            <div
              class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
            >
              <div v-if="form.imageUrl" class="aspect-[4/3] bg-gray-100">
                <img
                  :src="form.imageUrl"
                  :alt="form.title"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                v-else
                class="aspect-[4/3] bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center"
              >
                <svg
                  class="w-12 h-12 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div class="p-4 text-center">
                <p class="font-semibold text-gray-900 mb-1">{{ form.title }}</p>
                <p v-if="form.description" class="text-sm text-gray-500 mb-2">
                  {{ form.description }}
                </p>
                <p class="text-lg font-bold text-blue-600">
                  {{ formatPrice(form.price) }} {{ getCurrencyLabel(form.currency) }}
                </p>
                <span
                  class="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 mt-2"
                >
                  {{ getCategoryLabel(form.category) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-4">
          <router-link
            to="/admin/dashboard"
            class="flex-1 px-6 py-3.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-center"
          >
            Annuler
          </router-link>
          <button
            type="submit"
            :disabled="loading"
            class="flex-1 px-6 py-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading">Création...</span>
            <span v-else>Créer la carte</span>
          </button>
        </div>
      </form>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { Countries } from '@/app/features/shared/libraries/countries';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { $constants } = proxy;

// Import all countries from the Countries library
const allCountriesList = Countries.get();
const countries = ref(
  allCountriesList
    .filter((country) => country && country.countryName && country.countryCode) // Filter out null/invalid countries
    .sort((a, b) => a.countryName.localeCompare(b.countryName, 'fr'))
);
const form = ref({
  title: '',
  description: '',
  price: '',
  category: '',
  currency: 'USD',
  imageUrl: '',
  stock: 0,
  allowedCountries: countries.value.map((c) => c.countryCode)
});

const redeemCodes = ref([]);
const loading = ref(false);
const countrySearch = ref('');
const countriesExpanded = ref(false);

const allCountriesSelected = computed(() => {
  return form.value.allowedCountries.length === countries.value.length;
});

const filteredCountries = computed(() => {
  if (!countrySearch.value) {
    return countries.value;
  }
  const searchLower = countrySearch.value.toLowerCase();
  return countries.value.filter(
    (country) =>
      country.countryName.toLowerCase().includes(searchLower) ||
      country.countryCode.toLowerCase().includes(searchLower)
  );
});

onMounted(() => {
  // Check authentication
  const token = localStorage.getItem('adminToken');
  if (!token) {
    router.push('/admin/login');
  }
});

const toggleAllCountries = () => {
  if (allCountriesSelected.value) {
    form.value.allowedCountries = [];
  } else {
    form.value.allowedCountries = countries.value.map((c) => c.countryCode);
  }
};

// Generate flag emoji from country code
const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🏳️';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const updateStock = () => {
  form.value.stock = redeemCodes.value.length;
};

const addRedeemCode = () => {
  redeemCodes.value.push('');
  updateStock();
};

const removeRedeemCode = (index) => {
  redeemCodes.value.splice(index, 1);
  updateStock();
};

const handleSubmit = async () => {
  // Check if there are redeem codes
  if (redeemCodes.value.length === 0) {
    alert('Veuillez ajouter au moins un code de recharge');
    return;
  }

  // Validate redeem codes
  const emptyCodes = redeemCodes.value.filter(
    (code) => !code || code.trim() === ''
  );
  if (emptyCodes.length > 0) {
    alert('Veuillez remplir tous les codes de recharge');
    return;
  }

  // Validate countries
  if (form.value.allowedCountries.length === 0) {
    alert('Veuillez sélectionner au moins un pays');
    return;
  }

  loading.value = true;

  try {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${$constants.ENDPOINTS.LOCAL_API}/custom-cards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        title: form.value.title,
        description: form.value.description,
        price: form.value.price,
        category: form.value.category,
        currency: form.value.currency,
        imageUrl: form.value.imageUrl,
        stockQuantity: form.value.stock,
        allowedCountries: form.value.allowedCountries,
        availableCodes: redeemCodes.value
      })
    });

    const data = await response.json();

    if (response.ok) {
      // Redirect to dashboard on success
      router.push('/admin/dashboard');
    } else {
      alert(data.message || 'Erreur lors de la création de la carte');
    }
  } catch (err) {
    alert(
      'Erreur de connexion au serveur. Vérifiez que le serveur API est démarré.'
    );
    console.error('Create card error:', err);
  } finally {
    loading.value = false;
  }
};

const formatPrice = (price) => {
  if (!price) return '0';
  return new Intl.NumberFormat('fr-FR').format(Math.round(price));
};

const getCurrencyLabel = (currency) => {
  const labels = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'XOF': 'FCFA',
    'XAF': 'FCFA',
    'CAD': 'C$'
  };
  return labels[currency] || currency;
};

const getCategoryLabel = (category) => {
  const labels = {
    'Payment Cards': 'Paiement',
    'Gaming': 'Jeux vidéo',
    'Entertainment': 'Divertissement',
    'Shopping': 'Shopping',
    'Other': 'Autres'
  };
  return labels[category] || category || 'Non catégorisé';
};
</script>
